import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { Button } from "@/components/site/Button";
import { ContactForm } from "@/components/site/ContactForm";
import { CallbackBar } from "@/components/site/CallbackBar";
import { ExitIntentOffer } from "@/components/site/ExitIntentOffer";
import { TrustBand } from "@/components/site/TrustBand";
import { CaseCard } from "@/components/site/case/CaseCard";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";
import { ReelMosaic } from "@/components/site/case/ReelMosaic";
import { featuredCase, caseItems, heroStats, industries } from "@/content/cases";
import { reelVideos, reelCounts } from "@/content/work-reel";
import { cn } from "@/lib/utils";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { videoObject, aeoGeo, faqPage, speakable, breadcrumbs } from "@/lib/seo/schema";


const TITLE = "Case: filmproduktion och resultat | Fenny";
const DESCRIPTION = "Se Fennys filmcase inom reklamfilm, varumärkesfilm och kampanj. Utforska filmerna och hur produktionerna gjordes.";
const QUICK_ANSWER = "Fenny visar case inom filmproduktion: reklamfilm, brandfilm, kampanjfilm och löpande produktion. Varje case redovisar uppdrag, upplägg och leverans.";
const CASE_FAQ = [{ q: "Vad kostar en filmproduktion?", a: "Film offereras per uppdrag utifrån omfattning, antal inspelningsdagar och användningsrätt. Referensnivåer finns i Prisindex 2026 för filmproduktion." }];

export const Route = createFileRoute("/case")({
  head: () => ({

    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      {
        property: "og:description",
        content: "Case från Fenny — filmproduktioner med upplägg, leverans och resultat.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/case" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/case" }],
    // FEN-015: VideoObject för featured-filmen + ItemList av top-8 case-filmer.
    // Drar Google video-rich-results och AI Overviews.
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/case", name: TITLE, description: DESCRIPTION, question: "Vilka case har Fenny inom filmproduktion?", answer: QUICK_ANSWER, speakableSelectors: ["[data-quick-answer]"]})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          videoObject({
            name: `${featuredCase.title} — ${featuredCase.client}`,
            description: featuredCase.excerpt ?? featuredCase.title,
            thumbnailUrl: `https://i.ytimg.com/vi/${featuredCase.id}/hqdefault.jpg`,
            uploadDate: featuredCase.year ? `${featuredCase.year}-01-01` : "2024-01-01",
            embedUrl: `https://www.youtube-nocookie.com/embed/${featuredCase.id}`,
            contentUrl: `https://www.youtube.com/watch?v=${featuredCase.id}`,
          }),
        ),
      },
      {
        // Hela arbetsreelen som VideoObject-lista — även filmerna utan eget case-uppslag.
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Filmreel — Fenny",
          itemListElement: reelVideos.map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "VideoObject",
              name: v.title,
              description: v.title,
              thumbnailUrl: `https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`,
              uploadDate: "2025-01-01",
              embedUrl: `https://www.youtube-nocookie.com/embed/${v.youtubeId}`,
              contentUrl: `https://www.youtube.com/watch?v=${v.youtubeId}`,
            },
          })),
        }),
      },

      { type: "application/ld+json", children: JSON.stringify(faqPage(CASE_FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable(["[data-quick-answer]"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Case", path: "/case" },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Branschstudier — Fenny",
          itemListElement: [
            { name: "Prisindex 2026 — filmproduktion", url: "https://fenny.se/branschstudier/prisindex-2026" },
            { name: "Filmproduktionsbarometern", url: "https://fenny.se/branschstudier/filmproduktionsbarometern" },
          ].map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.name, url: x.url })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: caseItems.slice(0, 8).map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://fenny.se/case/${c.slug}`,
            name: `${c.title} — ${c.client}`,
          })),
        }),
      },
    ],
  }),
  component: CasePage,
});


function CasePage() {
  const [filter, setFilter] = useState<Category>("Alla");
  const [industry, setIndustry] = useState<string>("Alla branscher");

  const filtered = useMemo(() => {
    let pool = caseItems;
    if (filter !== "Alla") pool = pool.filter((i) => i.category === filter);
    if (industry !== "Alla branscher") pool = pool.filter((i) => i.industry === industry);
    return pool;
  }, [filter, industry]);


  return (
    <div>
      
      <EditorialHero
        eyebrow="Case"
        title="Arbeten som levererar resultat."
        highlight={[2, 3]}
        tone="butter"
        lede="Filmproduktioner inom reklam, varumärke, kampanj och löpande innehåll. Utforska våra arbeten nedan."
      />

      <div className="container-wide -mt-6 pb-8 md:-mt-10 md:pb-10">
        <p data-quick-answer className="max-w-3xl text-sm text-muted-foreground">{QUICK_ANSWER}</p>
        <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{reelCounts.videos} filmer i reelen. Klicka på en ruta för att se filmen.</p>
      </div>

      {/* Arbetsreel */}
      <Section className="pt-0">
        <ReelMosaic items={reelVideos} limit={12} className="reel-fade" />
      </Section>

      <FilmCases filtered={filtered} filter={filter} setFilter={setFilter} industry={industry} setIndustry={setIndustry} />

      <SignatureCta
        eyebrow="Vill ni bli nästa?"
        title="Boka mötet. Vi gör filmerna."
        body="30 minuter där vi pratar mål, kanaler och hur filmen ska användas."
        tone="butter"
      />

      <CallbackBar
        title="Vill ni ha ett liknande case?"
        body="Lämna namn och nummer så ringer vi upp samma arbetsdag och går igenom vad ett liknande upplägg skulle kosta."
      />

      <ContactForm id="kontakt" location="case" />

      <ExitIntentOffer location="case" ctaHref="#kontakt" />
    </div>
  );
}

type Category = "Alla" | "Reklam" | "Brand" | "Kampanj" | "Case";
const categories: Category[] = ["Alla", "Reklam", "Brand", "Kampanj", "Case"];

function FilmCases({
  filtered,
  filter,
  setFilter,
  industry,
  setIndustry,
}: {
  filtered: typeof caseItems;
  filter: Category;
  setFilter: (c: Category) => void;
  industry: string;
  setIndustry: (i: string) => void;
}) {
  return (
    <div>



      <Section className="pt-0">
        <div className="grid grid-cols-2 gap-6 border-y border-white/10 py-8 md:grid-cols-4 md:gap-10 md:py-10">
          {heroStats.map((s) => (
            <div key={s.label}>
              <div className="text-4xl font-semibold leading-none text-butter md:text-5xl">
                <LiveCountUp value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <TrustBand ctaLocation="case_trust_band_top" />

      {/* Featured */}
      <Section className="py-10 md:py-16">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-center">
          <VideoEmbed id={featuredCase.id} title={featuredCase.title} priority="high" />
          <div>
            <div className="mb-3 text-xs uppercase tracking-widest text-mint">
              Featured case · {featuredCase.service} · {featuredCase.format}
            </div>
            <h2 className="text-3xl font-semibold leading-tight md:text-5xl">{featuredCase.title}</h2>
            <p className="mt-5 text-base text-muted-foreground md:text-lg">{featuredCase.excerpt}</p>
            <dl className="mt-7 grid grid-cols-3 gap-4 border-y border-white/10 py-5">
              <div>
                <dt className="text-[11px] uppercase tracking-widest text-muted-foreground">Bransch</dt>
                <dd className="mt-1 text-sm font-semibold">{featuredCase.industry}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-widest text-muted-foreground">Tempo</dt>
                <dd className="mt-1 text-sm font-semibold">Löpande Prime</dd>
              </div>
              {featuredCase.metric && (
                <div>
                  <dt className="text-[11px] uppercase tracking-widest text-muted-foreground">{featuredCase.metric.label}</dt>
                  <dd className="mt-1 text-sm font-semibold text-mint">{featuredCase.metric.value}</dd>
                </div>
              )}
            </dl>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button to="/prime" variant="mint" arrow>Liknande produktioner</Button>
              <Button to="/kontakt" variant="outline">Diskutera ert projekt</Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Grid with filters */}
      <Section className="bg-surface/40">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <EyebrowHeading eyebrow="Allt arbete · film" title="Bläddra bland filmproduktionerna" />
        </div>

        <div className="mt-8 space-y-3">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm transition-colors",
                  filter === c ? "border-mint bg-mint text-mint-foreground" : "border-white/15 text-foreground/80 hover:border-white/35 hover:text-foreground",
                )}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {industries.map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndustry(i)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors",
                  industry === i ? "border-peach bg-peach/10 text-peach" : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-foreground",
                )}
              >
                {i}
              </button>
            ))}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((i, idx) => <CaseCard key={i.id} item={i} eager={idx < 3} />)}
          </div>
        ) : (
          <div className="mt-12 rounded-3xl border border-white/10 bg-surface/40 p-10 text-center text-muted-foreground">
            Inga case matchar filtren just nu — prova en annan kombination.
          </div>
        )}
      </Section>

      {/* Branschstudier — öppna data bakom casen */}
      <Section>
        <EyebrowHeading
          eyebrow="Branschstudie"
          title="Siffrorna bakom filmproduktion"
        />
        <p className="mt-5 max-w-3xl text-muted-foreground">
          Vi publicerar öppna branschstudier över den svenska marknaden för film — prisintervall, leveranstider och formatanvändning, med
          nedladdningsbar data. De ligger till grund för hur vi offererar
          uppdragen på den här sidan.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            {
              to: "/branschstudier/prisindex-2026",
              title: "Prisindex 2026 — filmproduktion",
              body: "Prisintervall och ledtider för reklamfilm, brandfilm och kampanj i Sverige 2026.",
            },
            {
              to: "/branschstudier/filmproduktionsbarometern",
              title: "Filmproduktionsbarometern",
              body: "Hur svenska bolag planerar, budgeterar och beställer rörligt material.",
            },
          ].map((c) => (
            <a
              key={c.to}
              href={c.to}
              className="rounded-3xl border border-white/10 bg-surface/40 p-6 transition-colors hover:border-mint/50"
            >
              <h3 className="text-lg font-semibold">{c.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              <span className="mt-4 inline-block text-sm text-mint">Läs studien →</span>
            </a>
          ))}
        </div>
      </Section>

      {/* Vanliga frågor */}
      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Vanliga frågor" title="Filmproduktion och pris" />
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {CASE_FAQ.map((f) => (
            <div key={f.q} className="rounded-3xl border border-white/10 bg-background/40 p-6">
              <h3 className="text-base font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

